import { inject } from 'mobx-react';
import * as React from 'react';

import { AnalyticsStore } from 'bernie-core';
import { ClientLogger } from 'bernie-client';
import { ErrorBoundary } from 'bernie-view';
import { LocalizedComponentProps, withLocalization } from 'bernie-l10n';

import { mapProperty } from 'src/common/stores/adapters';
import { PropertyListingProvider } from 'src/common/stores/contexts';

import { PropertySearchTravelAds } from 'src/common/__generated__/api/types';

import { UitkCarousel } from 'uitk-react-carousel';
import { UitkHeading } from 'uitk-react-text';
import { UitkPlaceholderCard } from 'uitk-react-cards';
import { UitkSpacing } from 'uitk-react-spacing';

import { fetchAndLogStatus } from 'utils/fetch-status-utils';

import { Lazy } from '../../common/lazy/lazy';
import { Listing } from 'components/search/listing';
import { TabGroupContext } from 'components/common/tab-group';

export interface SimilarHotelsProps extends LocalizedComponentProps {
  similarHotels: PropertySearchTravelAds.Properties[];
  analytics?: AnalyticsStore;
  isDatelessPdp?: boolean;
}

@withLocalization
@inject('analytics')
export class SimilarHotels extends React.Component<SimilarHotelsProps> {
  private logger = ClientLogger.getLoggerWithIdentifier('pwa.component.similar-hotels');

  private fireImpressionBeacon = (index: number) => {
    const hotel = mapProperty(this.props.similarHotels[index]);

    if (hotel.travelAd && hotel.travelAd.impressionTrackingUrl) {
      const impressionUrl = hotel.travelAd.impressionTrackingUrl;
      fetchAndLogStatus(impressionUrl, this.logger, 'TRAVELAD_IMPRESSION_FAILURE', 'TRAVELAD_IMPRESSION_SUCCESS');
    }
  };

  public render() {
    const { analytics, l10n, similarHotels, isDatelessPdp } = this.props;

    if (!similarHotels || similarHotels.length === 0) {
      return null;
    }

    const carouselName = 'HIS-similar-hotels';
    const recommenderCarouselProps = {
      carouselName,
      itemsVisible: { lg: 3, md: 2, sm: 1 },
      pageBy: 1,
      peekWidth: { lg: 0, md: 0, sm: 12 },
      analytics: analytics.setupTracker(carouselName, 'Carousel interaction'),
      buttonText: {
        nextButton: l10n.formatText('carousel.nextItem'),
        prevButton: l10n.formatText('carousel.previousItem'),
      },
      wrapItems: false,
    };

    const placeholderCarouselProps = { ...recommenderCarouselProps, itemsVisible: { lg: 2, md: 2, sm: 1 } };
    const headline = this.props.isDatelessPdp
      ? l10n.formatText('hotels.dateless.infosite.similarPropertiesCarousel.headline')
      : l10n.formatText('hotels.infosite.recommenderCarousel.headline');

    return (
      <ErrorBoundary label={`hotel.similarHotels${isDatelessPdp ? 'Dateless' : ''}`}>
        <TabGroupContext.Consumer>
          {() => (
            <Lazy>
              <Lazy.Placeholder>
                <UitkCarousel {...placeholderCarouselProps}>
                  <li>
                    <UitkPlaceholderCard />
                  </li>
                  <li>
                    <UitkPlaceholderCard />
                  </li>
                </UitkCarousel>
              </Lazy.Placeholder>
              <Lazy.Loaded>
                <div>
                  <UitkSpacing margin={{ blockstart: 'three' }}>
                    <UitkHeading tag="h4" size={4}>
                      {headline}
                    </UitkHeading>
                  </UitkSpacing>
                  <UitkSpacing margin={{ blockstart: 'three' }}>
                    <UitkCarousel itemViewedCallback={this.fireImpressionBeacon} {...recommenderCarouselProps}>
                      {similarHotels.map((hotel, index) => {
                        const hotelViewModel = mapProperty(hotel);
                        hotelViewModel.primaryCardMessagingModel = null;
                        if (hotelViewModel.travelAd) {
                          hotelViewModel.travelAd.carouselImpressionTrackingUrl = hotelViewModel.travelAd.impressionTrackingUrl;
                          hotelViewModel.travelAd.impressionTrackingUrl = null;
                        }

                        return (
                          <PropertyListingProvider propertyListing={hotel} key={`carousel-item-${index}`}>
                            <Listing
                              analytics={analytics}
                              className="listing uitk-carousel-item uitk-flex-grow-1"
                              defaultListingOnly={true}
                              model={hotelViewModel}
                              referralObject={{ navigation: { rfrr: `HOT.HIS:hotels.infosite.HIS-similar-hotels.${index + 1}` } }}
                              isDatelessPdp={Boolean(this.props.isDatelessPdp)}
                              excludePriceBreakDownLink={true}
                            />
                          </PropertyListingProvider>
                        );
                      })}
                    </UitkCarousel>
                  </UitkSpacing>
                </div>
              </Lazy.Loaded>
            </Lazy>
          )}
        </TabGroupContext.Consumer>
      </ErrorBoundary>
    );
  }
}
